import { useLiveQuery } from 'dexie-react-hooks';
import keyBy from 'lodash/keyBy';
import moment from 'moment';
import { useCallback, useMemo } from 'react';
import { UseQueryOptions } from 'react-query';
import {
  ProjectId,
  Team,
  User,
  UserStatus,
  Order,
  OrderType,
  UserType,
} from 'api/models';
import { PartnerPermissionName } from 'api/partner/types';
import { Project } from 'api/project';
import { ProjectInUserProfile } from 'api/user';
import { Views } from 'components/molecules/views';
import { FormResponse } from 'components/organisms/forms/utils';
import { ColumnOrder } from 'components/organisms/grid-custom-column';
import { useUser } from 'hooks/use-user';
import { ApiErrorData } from 'queries/error';
import { activeUsersAndTeamsFromIndexedB } from 'queries/members';
import { useProjectDetailQuery } from 'queries/project';
import { logger } from 'services';
import { fullName } from 'utils/full-name';
import { unreachable } from 'utils/unreachable';
import { db } from 'workers';
import { FormResponseDraft } from 'workers/models/form-responses';

// TODO: Check if all the functions in this file can be executed inside the worker
const filterActiveUsers = (user) => {
  return user.userStatus !== UserStatus.Inactive;
};

export function useAddProjectMember(member: User): number | undefined {
  const data = useLiveQuery(() => {
    return db.members.add(member);
  });

  return data;
}

export function usePrerenderMembersAndTeamsFromIndexedDb(): void {
  const members = useLiveQuery(() => {
    return db.members.filter(filterActiveUsers).toArray();
  });

  const teams = useLiveQuery(() => {
    return db.teams.toArray();
  });

  activeUsersAndTeamsFromIndexedB.setActiveUsersAndTeams = {
    users: (members as User[]) || [],
    teams: (teams as Team[]) || [],
  };
}

export function useGetAllNativeUsersFromIndexedDb(): User[] {
  const data = useLiveQuery(() => {
    return db.members
      .where({
        userType: UserType.Native,
      })
      .filter(filterActiveUsers)
      .toArray();
  });

  return data || [];
}

export function useGetAllMembersBasedonFilter(
  filter: Record<string, unknown>,
): User[] {
  const data = useLiveQuery(() => {
    return db.members.where(filter).filter(filterActiveUsers).toArray();
  });

  return data || [];
}

export function useGetTeamsByProjectId(projectIds: number[]): {
  data: Record<string, Team[]>;
  isLoading: boolean;
} {
  const data = useLiveQuery(async () => {
    return db.teams
      .toArray()
      .then((teams) => {
        const teamsByProjectIds: Record<string, Team[]> = {};

        projectIds.forEach((projectId) => {
          const teamList = teams.filter((t) =>
            t.projectIds?.includes?.(projectId),
          );

          teamsByProjectIds[projectId] = teamList;
        });

        return teamsByProjectIds;
      })
      .catch((err) => {
        logger.error('Error occured while fetching data from indexed db', err);
      });
  }, [projectIds]);

  return useMemo(
    () =>
      data != null ? { data, isLoading: false } : { data: {}, isLoading: true },
    [data],
  );
}

export function useGetAllMembersInProjects({
  projectIds,
  isPrivate,
}: {
  projectIds: number[];
  isPrivate: boolean;
}): User[] {
  const conditionalFilter = (user: User) => {
    let filter = filterActiveUsers(user);

    if (isPrivate) {
      filter = filter && user.userType === UserType.Native;
    }

    return filter;
  };

  const data = useLiveQuery(async () => {
    return db.members
      .where('projectIds')
      .anyOf(projectIds)
      .filter(conditionalFilter)
      .distinct()
      .toArray();
  }, [projectIds]);

  return data || [];
}

export function useGetAllMembersNotInProjects({
  projectIds,
  isPrivate,
}: {
  projectIds: number[];
  isPrivate: boolean;
}): User[] {
  const conditionalFilter = (user: User) => {
    let filter = filterActiveUsers(user);

    if (isPrivate) {
      filter = filter && user.userType === UserType.Native;
    }

    return filter;
  };

  const data = useLiveQuery(() => {
    return db.members
      .where('projectIds')
      .anyOf(projectIds)
      .filter(conditionalFilter)
      .toArray();
  }, [projectIds]);

  return data || [];
}
// having this to avoid circular dependency
export const useProjectDetail = (
  projectId: ProjectId,
  options: UseQueryOptions<Project, ApiErrorData, Project, ProjectId> = {},
) => {
  return useProjectDetailQuery(projectId, {
    ...options,
    retry: 1,
    retryOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: false,
    enabled: typeof projectId?.[1] === 'number',
  });
};

interface ProjectMembersOptions {
  includeRestricedPartnersMembers?: boolean;
}

export function useGetAllMembersInProject({
  isPrivate = false,
  projectId,
  options = {
    includeRestricedPartnersMembers: true,
  },
}: {
  projectId?: number;
  isPrivate?: boolean;
  options?: ProjectMembersOptions;
}): User[] {
  const { data: project } = useProjectDetail(ProjectId(projectId ?? -1));
  const permissivePartnerCompanyIdsInProject = useMemo(
    () =>
      (project?.partners ?? [])
        .filter(
          (partner) =>
            partner.accessLevel?.partnerPermissionSetName ===
            PartnerPermissionName.Permissive,
        )
        .map((partner) => partner.companyId),
    [project?.partners],
  );
  const restricedPartnerCompanyIdsInProject = useMemo(
    () =>
      (project?.partners ?? [])
        .filter(
          (partner) =>
            partner.accessLevel?.partnerPermissionSetName ===
            PartnerPermissionName.Restricted,
        )
        .map((partner) => partner.companyId),
    [project?.partners],
  );
  const filterForPrivateResource = useCallback(
    (user: User) =>
      user.userType === UserType.Native ||
      (user.userType === UserType.Partner &&
        permissivePartnerCompanyIdsInProject.includes(user.company.companyId)),
    [permissivePartnerCompanyIdsInProject],
  );
  const filterOutRestrictedPartners = useCallback(
    (user: User) => {
      return (
        user.userType !== UserType.Partner ||
        !restricedPartnerCompanyIdsInProject.includes(user.company.companyId)
      );
    },
    [restricedPartnerCompanyIdsInProject],
  );
  const conditionalFilter = useCallback(
    (user: User) =>
      filterActiveUsers(user) &&
      (options.includeRestricedPartnersMembers === true ||
        filterOutRestrictedPartners(user)) &&
      (isPrivate ? filterForPrivateResource(user) : true),
    [
      filterForPrivateResource,
      filterOutRestrictedPartners,
      isPrivate,
      options.includeRestricedPartnersMembers,
    ],
  );

  const data = useLiveQuery(() => {
    if (!projectId) {
      return [];
    }

    return db.members
      .where('projectIds')
      .equals(projectId)
      .filter(conditionalFilter)
      .toArray();
  }, [projectId, conditionalFilter]);

  return data || [];
}

export function useGetAllActiveUsers(): User[] {
  const data = useLiveQuery(() => {
    return db.members
      .where('userStatus')
      .notEqual(UserStatus.Inactive)
      .toArray();
  });

  return data || [];
}

export function useGetAllActiveUsersMap(): Record<number, User> {
  const users = useLiveQuery(() => {
    return db.members
      .where('userStatus')
      .notEqual(UserStatus.Inactive)
      .toArray();
  });

  return keyBy(users, 'userId') || {};
}

export function useGetAllNativeUsersMap(): Record<number, User> {
  const users = useLiveQuery(() => {
    return db.members
      .where('userStatus')
      .notEqual(UserStatus.Inactive)
      .filter((user) => user.userType === UserType.Native)
      .toArray();
  });

  return keyBy(users, 'userId') || {};
}

// if UserType === NATIVE then load all native users who are not in this project
// if UserType === EXTERNAL then load all cusatomer users who are not in this project
export function useGetAllMembersNotInProject({
  projectId,
  isPrivate = false,
  options = {
    includeRestricedPartnersMembers: true,
  },
}: {
  projectId?: number;
  isPrivate?: boolean;
  options?: ProjectMembersOptions;
}): User[] {
  const { data: project } = useProjectDetail(ProjectId(projectId ?? -1));
  const customerCompanyId = project?.customer.companyId;
  const { user: currentUser } = useUser();
  const currentUserCompanyFromProjectPartners = useMemo(
    () =>
      currentUser.userType === UserType.Partner
        ? (project?.partners ?? []).find(
            (partner) => partner.companyId === currentUser.company.companyId,
          )
        : undefined,
    [currentUser.company.companyId, currentUser.userType, project?.partners],
  );
  const partnersInProject = useMemo(
    () => (project?.partners ?? []).map(({ companyId }) => companyId),
    [project?.partners],
  );

  const restricedPartnerCompanyIdsInProject = useMemo(
    () =>
      (project?.partners ?? [])
        .filter(
          ({ accessLevel }) =>
            accessLevel?.partnerPermissionSetName ===
            PartnerPermissionName.Restricted,
        )
        .map(({ companyId }) => companyId),
    [project?.partners],
  );

  const partnersWhoCanAccessPrivateResources = useMemo(
    () =>
      (project?.partners ?? [])
        .filter(
          ({ accessLevel }) =>
            accessLevel?.partnerPermissionSetName ===
            PartnerPermissionName.Permissive,
        )
        .map(({ companyId }) => companyId),
    [project?.partners],
  );

  const canBeInvitedByVendorOnPrivateResource = useCallback(
    (user: User) =>
      user.userType === UserType.Native ||
      (user.userType === UserType.Partner &&
        partnersWhoCanAccessPrivateResources.includes(user.company.companyId)),
    [partnersWhoCanAccessPrivateResources],
  );
  const canBeInvitedByVendorOnSharedesource = useCallback(
    (user: User) =>
      user.userType === UserType.Native ||
      (user.userType === UserType.Partner &&
        partnersInProject.includes(user.company.companyId) &&
        (options.includeRestricedPartnersMembers === true ||
          !restricedPartnerCompanyIdsInProject.includes(
            user.company.companyId,
          ))) ||
      (user.userType === UserType.External &&
        user.company.companyId === customerCompanyId),
    [
      customerCompanyId,
      options.includeRestricedPartnersMembers,
      partnersInProject,
      restricedPartnerCompanyIdsInProject,
    ],
  );

  const canBeInvitedByVendor = useCallback(
    (user: User) =>
      isPrivate
        ? canBeInvitedByVendorOnPrivateResource(user)
        : canBeInvitedByVendorOnSharedesource(user),

    [
      canBeInvitedByVendorOnPrivateResource,
      canBeInvitedByVendorOnSharedesource,
      isPrivate,
    ],
  );
  const canBeInvitedByCustomer = useCallback(
    (user: User) =>
      isPrivate ? false : user.company.companyId === customerCompanyId,
    [customerCompanyId, isPrivate],
  );
  const canBeInvitedByPartner = useCallback(
    (user: User) => {
      if (currentUserCompanyFromProjectPartners == null) {
        return false;
      }
      if (
        isPrivate &&
        currentUserCompanyFromProjectPartners.accessLevel
          ?.partnerPermissionSetName !== PartnerPermissionName.Permissive
      ) {
        return false;
      }

      return user.company.companyId === currentUser.company.companyId;
    },
    [
      currentUser.company.companyId,
      currentUserCompanyFromProjectPartners,
      isPrivate,
    ],
  );
  const canBeInvitedByCurrentUser = useCallback(
    (user: User) => {
      switch (currentUser.userType) {
        case UserType.Native:
          return canBeInvitedByVendor(user);
        case UserType.External:
          return canBeInvitedByCustomer(user);
        case UserType.Partner:
          return canBeInvitedByPartner(user);
        default:
          return unreachable(currentUser.userType);
      }
    },
    [
      currentUser.userType,
      canBeInvitedByCustomer,
      canBeInvitedByPartner,
      canBeInvitedByVendor,
    ],
  );

  const conditionalFilter = useCallback(
    (user: User) => {
      if (!projectId) {
        return false;
      }

      return (
        !user.projectIds?.includes(projectId) &&
        filterActiveUsers(user) &&
        canBeInvitedByCurrentUser(user)
      );
    },
    [canBeInvitedByCurrentUser, projectId],
  );

  const data = useLiveQuery(() => {
    if (!projectId) {
      return [];
    }

    return db.members.filter(conditionalFilter).toArray();
  }, [conditionalFilter]);

  return data || [];
}

export function useGetAllMembersByCompany(companyId: number): User[] {
  const data = useLiveQuery(async () => {
    if (!companyId) {
      return undefined;
    }

    return db.members
      .where('company.companyId')
      .equals(companyId)
      .filter(filterActiveUsers)
      .toArray();
  }, [companyId]);

  return data || [];
}

export function useGetAllTeamsFromIndexedDb(): Team[] {
  const data = useLiveQuery(() => {
    return db.teams.toArray();
  });

  return data || [];
}

export function useGetAllTeamsFromIndexedDbConditionally(
  projectId?: number,
): Team[] {
  const data = useLiveQuery(async () => {
    if (projectId) {
      return db.teams
        .filter((team) => !!team?.projectIds?.includes?.(projectId))
        .toArray();
    }

    return db.teams.filter((team) => !!team?.accountLevelTeam).toArray();
  });

  return data || [];
}

export function useGetUserFromIndexedDb(userId?: number): User {
  const data = useLiveQuery(() => {
    return userId
      ? db.members
          .where({
            userId,
          })
          .first()
      : undefined;
  });

  return data || ({} as User);
}

export async function getUserFromIndexedDb(
  userId: number,
): Promise<User | undefined> {
  const user = await db.members
    .where({
      userId,
    })
    .first();

  return user;
}

export function useGetProjectIdsOfCurrentUserFromIndexedDb(): number[] {
  const {
    user: { userId },
  } = useUser();
  const data = useLiveQuery(() => {
    return userId
      ? db.members
          .where({
            userId,
          })
          .first()
      : undefined;
  });

  return data?.projectIds || [];
}

export function useGetUsersFromIndexedDb(userIds: number[]): User[] | [] {
  const data = useLiveQuery(() => {
    return userIds.length
      ? db.members.where('userId').anyOf(userIds).toArray()
      : [];
  });

  return data || [];
}

export function useGetTeamsFromIndexedDb(teamIds: number[]): Team[] | [] {
  const data = useLiveQuery(() => {
    return teamIds.length
      ? db.teams.where('teamId').anyOf(teamIds).toArray()
      : [];
  });

  return data || [];
}

export function useFullName(userId?: number): string {
  const user = useGetUserFromIndexedDb(userId);

  return fullName(user as User);
}

async function getAllColumnOrderFromIndexedDb(
  orderType: OrderType,
): Promise<Order[]> {
  const data = await db.columnOrder.where({ orderType }).toArray();

  return data;
}

export async function getColumnOrderFromIndexedDb(key: string): Promise<Order> {
  const data = await db.columnOrder.where({ key }).toArray();

  return data[0];
}

export async function addUpdateColumnOrderIndexedDb(
  key: string,
  viewId: number,
  columnOrder: ColumnOrder[],
  orderType: OrderType,
): Promise<string> {
  const data = await db.columnOrder.put(
    {
      key,
      viewId,
      columnOrder,
      orderType,
    },
    key,
  );

  return data;
}

export async function deleteColumnOrderFromIndexedDb<T>(
  orderType: OrderType,
  currentListViews: Views<T>,
): Promise<void> {
  const idbList = await getAllColumnOrderFromIndexedDb(orderType);

  if (idbList.length === 0 && currentListViews.ids[0] !== 'just') {
    return;
  }
  const deletedViewKeys = idbList
    .filter(
      (x) =>
        currentListViews.ids[0] === 'just' &&
        x.viewId >= 0 &&
        !currentListViews?.ids?.[1]?.includes(x.viewId),
    )
    .map((item) => item.key);

  if (deletedViewKeys.length > 0) {
    await db.columnOrder.bulkDelete(deletedViewKeys);
  }
}

export async function addProjectsToIndexDb(
  projects: ProjectInUserProfile[],
): Promise<void> {
  await db.projects.clear();
  await db.projects.bulkAdd(projects);
}

export const useProjectsLightFromIndexDb = (
  search: string,
): ProjectInUserProfile[] => {
  return (
    useLiveQuery(() =>
      db.projects
        .filter((project) => project.projectName.includes(search))
        .toArray(),
    ) || []
  );
};
export async function getFormResponseDraftFromIndexedDb(
  formInstance: number,
): Promise<FormResponseDraft> {
  const data = await db.formResponseDraft.where({ formInstance }).toArray();

  return data[0];
}

export async function addUpdateFormResponseDraftIndexedDb(
  formInstance: number,
  response: FormResponse,
): Promise<number> {
  const data = await db.formResponseDraft.put(
    {
      response,
      timestamp: moment().valueOf(),
      formInstance,
    },
    formInstance,
  );

  return data;
}

export async function deleteFormResponseFromIndexedDb(
  formInstance: number,
): Promise<void> {
  return db.formResponseDraft.delete(formInstance);
}
